// ContainerEditor — the Container block's settings pane, restyled to the WL-011
// framework with the row-39 Basic/Advanced split. WL-011 (Fable Hail Mary Wave 2).
//
// GORDON 2026-09-15 (Container block settings pane standardisation) rewrote the
// Advanced section to the app's shared standards, replacing 4 bespoke pieces:
//   1. Full Width now actually does something: it locks the container flush to
//      the segment-card edit edges (0 left/right margin, enforced both here AND
//      render-side in Container.jsx via containerFullWidth.lockedMargin) and
//      hides the Margin Left/Right steppers + the Corner Rounding control while
//      active.
//   3. Background is the app-standard fill control (ContainerFillControl: Colors
//      / Gradient / Patterns / Image tabs, brand swatches, Media Library, NO
//      Opacity slider) — unconditionally; the old CONTAINER_BACKGROUNDS_ENABLED-
//      gated bespoke ContainerBackgroundControl (Image/GIF·Color·Gradient +
//      Opacity) is no longer used here (it lives on, unchanged, for the
//      unrelated Title Bar block's own reuse of it).
//   4. The legacy "apply the same value to all sides" checkbox/fan-out
//      (applyPaddingAll/applyMarginAll + QuadStepper) is gone.
//   5. Padding and Margin both go through the SAME shared Shared/SpacingControl
//      PR#5531 introduced (one row of 4 independently-editable steppers, no
//      apply-all) instead of the local QuadStepper mirror, with the exact (i)
//      tooltip copy the note specifies.
//   6. Border and Drop Shadow use the standard brand-swatch control
//      (Shared/ColorPopup — the SAME one Accordion's Border/Content
//      Background/Drop Shadow use) instead of the bespoke single-swatch
//      ColorField.
//
// BASIC   = Size + Background Fill (a container's defining look).
// ADVANCED= Padding, Margin, Corner Rounding, Border, Drop Shadow.
//
// Controlled: takes `value` (the container style object) + `onChange(patch)`.
// The live craftjs Settings wrapper adopts this by mapping its setProp handlers
// onto onChange — see WL-011 remaining[] / ContainerEditorSettings.jsx.

import React from "react";
import BlockEditorPane from "../BlockEditorPane";
import EditorSection from "../EditorSection";
import AdvancedOptions from "../AdvancedOptions";
import { SegChoice, LabeledSlider } from "../controls";
import SpacingControl from "../../Shared/SpacingControl";
import ColorPopup from "../../Shared/ColorPopup";
import ContainerFillControl from "../../CodeBlocks/Container/ContainerFillControl";

const ContainerGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="5.5" width="17" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <rect x="6.5" y="8.5" width="11" height="7" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
  </svg>
);

const DEFAULT = {
  fullWidth: false,
  background: "#F3ECFF",
  containerFill: undefined,
  padding: { top: 8, right: 8, bottom: 8, left: 8 },
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  radius: 8,
  borderColor: "#E4E7EC",
  borderWidth: 0,
  dropShadowColor: "",
};

const ContainerEditor = ({
  value = DEFAULT,
  onChange = () => {},
  segmentName,
  onBack,
  status = "ready",
  onRetry,
  errorMessage,
}) => {
  const v = { ...DEFAULT, ...value };
  const patch = (p) => onChange({ ...v, ...p });

  const patchPaddingSide = (side, next) => patch({ padding: { ...v.padding, [side]: next } });
  const patchMarginSide = (side, next) => patch({ margin: { ...v.margin, [side]: next } });

  // GORDON LAW 2026-09-15 (gordon-0915-law-full-width-locks-edges-hides-margin-
  // rounding): Full Width locks the block flush to both edges (margin.left/right
  // = 0); turning it back off restores whatever left/right values were set
  // before, stashed on `prevMargin` so it survives a re-select, not just a
  // component remount.
  const handleSizeChange = (val) => {
    const goingFullWidth = val === "full";
    if (goingFullWidth === v.fullWidth) return;
    if (goingFullWidth) {
      patch({
        fullWidth: true,
        prevMargin: { left: v.margin.left, right: v.margin.right },
        margin: { ...v.margin, left: 0, right: 0 },
      });
    } else {
      const restore = v.prevMargin || { left: DEFAULT.margin.left, right: DEFAULT.margin.right };
      patch({
        fullWidth: false,
        margin: { ...v.margin, left: restore.left, right: restore.right },
      });
    }
  };

  return (
    <BlockEditorPane
      blockName="Container"
      segmentName={segmentName}
      glyph={<ContainerGlyph />}
      onBack={onBack}
      status={status}
      onRetry={onRetry}
      errorMessage={errorMessage}
    >
      <EditorSection heading="Basic">
        <SegChoice
          label="Size"
          value={v.fullWidth ? "full" : "content"}
          options={[
            { label: "Content Width", value: "content" },
            { label: "Full Width", value: "full" },
          ]}
          onChange={handleSizeChange}
        />
        <ContainerFillControl value={v} onPatch={patch} />
      </EditorSection>

      <AdvancedOptions>
        <SpacingControl
          label="Padding"
          tooltipDescription="the space between the container content and the edge of the container"
          value={v.padding}
          onChange={patchPaddingSide}
        />
        <SpacingControl
          label="Margin"
          tooltipDescription="the space outside the block"
          value={v.margin}
          onChange={patchMarginSide}
          fullWidth={v.fullWidth}
        />
        {!v.fullWidth && (
          <LabeledSlider
            label="Corner Rounding"
            value={v.radius}
            min={0}
            max={20}
            unit="px"
            onChange={(n) => patch({ radius: n })}
          />
        )}
        <ColorPopup
          title="Border"
          background={v.borderColor}
          handleChangeBackgroundColor={(c) => patch({ borderColor: c })}
          resetBackgroundColor={() => patch({ borderColor: DEFAULT.borderColor })}
          sliderBoxTitle="Border Width"
          sliderValue={v.borderWidth}
          sliderChange={(n) => patch({ borderWidth: n })}
          sliderMinValue={0}
          sliderMaxValue={8}
          sliderSymbol="px"
          isHideGradient
          showSuggestedColorPalette
          isBorderSlider
        />
        <ColorPopup
          title="Drop Shadow"
          background={v.dropShadowColor || "transparent"}
          handleChangeBackgroundColor={(c) => patch({ dropShadowColor: c })}
          resetBackgroundColor={() => patch({ dropShadowColor: "" })}
          showSlider={false}
          isHideGradient
          showSuggestedColorPalette
        />
      </AdvancedOptions>
    </BlockEditorPane>
  );
};

export default ContainerEditor;
